import { Box, Link } from '@material-ui/core';
import {
  cleanProductDetailError,
  shopifyProductDetails,
} from '@src/reducers/shopify';
import { TimelineEvent } from '@src/types';
import { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import {
  ShopifySeeProductDetailsIcon,
  ShopifyVisitStoreIcon,
} from '../SvgIcons';

interface IChatMessageProductPreviewWrapper {
  message: Partial<TimelineEvent>;
}

export type TChatMessageProductPreview = IChatMessageProductPreviewWrapper & {
  handleClickDetail: () => void;
};

const ChatMessageProductPreviewWrapper: React.FC<
  IChatMessageProductPreviewWrapper
> = ({ message }) => {
  const dispatch = useDispatch();

  return (
    <ChatMessageProductPreview
      message={{
        ...message,
        description: (message.description || '').replace(/\n/g, '<br />'),
      }}
      handleClickDetail={() => {
        const { information } = message;
        if (!information?.productId) {
          // eslint-disable-next-line no-console
          console.error('information.productId is not defined!');
          return;
        }

        dispatch(cleanProductDetailError());
        const { productId, locationId } = information;
        dispatch(shopifyProductDetails({ productId, locationId }));
      }}
    />
  );
};

export const ChatMessageProductPreview: React.FC<
  TChatMessageProductPreview
> = ({ message, handleClickDetail }) => {
  const { name, description, price, imageUrl, information, currencySymbol } =
    message;
  const formattedPrice = useCurrencyFormat({
    value: price,
    symbol: currencySymbol,
  });

  const linkStyle: CSSProperties = {
    flexDirection: 'row',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 600,
    cursor: 'pointer',
    gap: 12,
  };

  const hasTheRequiredData =
    information?.variants && information?.vendor && information?.collections;

  return (
    <Box style={{ maxWidth: 262, margin: '8px' }}>
      <Box
        style={{
          height: '180px',
          width: '262px',
          textAlign: 'center',
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
        }}
      >
        <img
          src={imageUrl}
          alt=""
          height="100%"
          width="100%"
          style={{ objectFit: 'scale-down' }}
        />
      </Box>
      <Box fontSize="14px" fontWeight="600" textAlign="center" padding="4px">
        {name}
      </Box>

      <Box fontSize="12px" paddingBottom={2}>
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: description || '' }} />
      </Box>
      {hasTheRequiredData && (
        <Box display="flex" flexDirection="column" style={{ gap: '4px' }}>
          {information?.variants.map(item => (
            <Box key={item.name}>
              {item.name}: {item.value}
            </Box>
          ))}
          <Box>
            <FormattedMessage id="Shopify.product.price.label" />{' '}
            {formattedPrice}
          </Box>
          <Box>
            <FormattedMessage id="Shopify.product.provider.label" />:{' '}
            {information?.vendor}
          </Box>
          {information?.collections.length > 0 && (
            <Box>
              <FormattedMessage id="Shopify.product.collections.label" />:{' '}
              {information?.collections.join(', ')}
            </Box>
          )}
        </Box>
      )}
      <Box marginTop={2}>
        <Box style={linkStyle}>
          {ShopifyVisitStoreIcon}
          <Link
            href={information?.productUrlVariant}
            target="_blank"
            rel="noreferrer"
            color="inherit"
            underline="none"
            style={{
              textDecoration: 'none',
            }}
          >
            <FormattedMessage id="Shopify.product.Link.visit.store.label" />
          </Link>
        </Box>
        <Box style={linkStyle} paddingTop="16px" onClick={handleClickDetail}>
          {ShopifySeeProductDetailsIcon}
          <FormattedMessage id="Shopify.product.Link.details.label" />
        </Box>
      </Box>
    </Box>
  );
};

export default ChatMessageProductPreviewWrapper;
